import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  MagnifyingGlassIcon, PlusIcon, ChevronDownIcon, ArrowPathIcon, TrashIcon, XMarkIcon, ChevronLeftIcon,
  ChevronRightIcon, CloudArrowUpIcon, LinkIcon, Squares2X2Icon, ListBulletIcon,
} from '@heroicons/react/24/outline'
import {
  fetchPublicApps, fetchTenantApps, fetchPublicAppFilters, fetchTenantAppFilters, deleteTenantApp, retryJob,
  syncAllSubscriptions, unsubscribe,
} from '../services/api'
import type { CatalogFilterValues } from '../services/api'
import { useAuthStore } from '../store/authStore'
import AddPackageModal from '../components/AddPackageModal'
import AddInstallerModal from '../components/AddInstallerModal'
import DeployWizard, { DeployApp } from '../components/DeployWizard'
import { TableCard, TableSkeleton } from '../components/ui'
import type { LearnFromSource } from '../types/job'
import { stripMarkdown } from '../utils/markdown'
import { tenantAPI, getPublicAppSourceTypes } from '../services/api'
import { sourceTypeLabel, sourceTypeTitle } from '../utils/sourceType'
import { errorText } from '../utils/errorText'
import { localDate } from '../utils/date'
import { CatalogTab, catalogTabKey, readCatalogTab, writeCatalogTab } from '../utils/catalogTab'
import {
  CatalogView, PAGE_SIZES, PageSize, readCatalogView, readPageSize, writeCatalogView, writePageSize,
} from '../utils/catalogDisplay'

/** Every package is normalised to one of these when it is built, so the filter offers a fixed list. */
const ARCHITECTURES = ['x64', 'x86', 'arm64']

/** "Published" presets, as the number of days back they reach. The empty value is no filter. */
const PUBLISHED_WITHIN = [
  { value: '', label: 'Any time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last year' },
]

/** The instant a "Published" preset reaches back to, resolved when the request is made. */
function publishedSince(days: string): string | undefined {
  if (!days) return undefined
  return new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000).toISOString()
}

/** One sorted list without repeats, from the values each catalog lists. */
function mergeValues(...lists: (string[] | undefined)[]): string[] {
  return Array.from(new Set(lists.flatMap((list) => list ?? []))).sort((a, b) => a.localeCompare(b))
}

/** The filter's own value stays selectable even when the catalog no longer lists it. */
function withCurrent(values: string[], current: string): string[] {
  return current && !values.includes(current) ? [current, ...values] : values
}

const SHORT_DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }

export default function AppStore() {
  const { user, tenantContext, isTenantAdmin, isSuperAdmin, hasPermission } = useAuthStore()
  const superAdmin = isSuperAdmin()

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [publisherFilter, setPublisherFilter] = useState('')
  const [architectureFilter, setArchitectureFilter] = useState('')
  const [publishedFilter, setPublishedFilter] = useState('')
  const hasFilters = !!(selectedCategory || publisherFilter || architectureFilter || publishedFilter)
  const clearFilters = () => {
    setSelectedCategory(null)
    setPublisherFilter('')
    setArchitectureFilter('')
    setPublishedFilter('')
  }
  // Page size and layout are remembered for this browser (see catalogDisplay.ts).
  const [pageSize, setPageSize] = useState<PageSize>(() => readPageSize())
  const selectPageSize = (size: PageSize) => {
    setPageSize(size)
    writePageSize(size)
  }
  const [view, setView] = useState<CatalogView>(() => readCatalogView())
  const selectView = (next: CatalogView) => {
    setView(next)
    writeCatalogView(next)
  }
  // Opens on the tab this user last used in this tenant, and on Public until they pick one.
  const tabKey = catalogTabKey(user?.id, tenantContext?.tenant_id)
  const [activeTab, setActiveTab] = useState<CatalogTab>(() => readCatalogTab(tabKey, superAdmin))
  const selectTab = (tab: CatalogTab) => {
    setActiveTab(tab)
    writeCatalogTab(tabKey, tab)
  }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const isAdmin = isTenantAdmin()
  const canManageApps = hasPermission('manage_apps')
  const canDeploy = hasPermission('deploy_apps')

  // Package modal state
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false)
  const [packageModalSource, setPackageModalSource] = useState<LearnFromSource>('winget')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Installer upload modal state
  const [isInstallerModalOpen, setIsInstallerModalOpen] = useState(false)

  // Delete modal state. A shortcut entry is a subscription to a certified public package, so
  // removing it deletes the subscription and leaves the public package itself alone.
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [appToDelete, setAppToDelete] = useState<
    { id: string; name: string; isShortcut: boolean; subscriptionId?: string } | null
  >(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Retry state
  const [retryingAppId, setRetryingAppId] = useState<string | null>(null)

  // Sync all subscriptions state
  const [isSyncingAll, setIsSyncingAll] = useState(false)
  const [syncAllMessage, setSyncAllMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // Pagination state
  const [publicPage, setPublicPage] = useState(1)
  const [privatePage, setPrivatePage] = useState(1)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleAddPackage = (source: LearnFromSource) => {
    setPackageModalSource(source)
    setIsPackageModalOpen(true)
    setIsDropdownOpen(false)
  }

  const handleUploadInstaller = () => {
    setIsInstallerModalOpen(true)
    setIsDropdownOpen(false)
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['public-apps'] }),
        queryClient.invalidateQueries({ queryKey: ['tenant-apps'] }),
        queryClient.invalidateQueries({ queryKey: ['public-app-filters'] }),
        queryClient.invalidateQueries({ queryKey: ['tenant-app-filters'] }),
      ])
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleDeleteClick = (app: { id: string; display_name: string; is_shortcut?: boolean; subscription_id?: string }) => {
    setAppToDelete({
      id: app.id,
      name: app.display_name,
      isShortcut: !!app.is_shortcut,
      subscriptionId: app.subscription_id,
    })
    setDeleteError(null)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!appToDelete) return

    setIsDeleting(true)
    setDeleteError(null)

    try {
      if (appToDelete.isShortcut && appToDelete.subscriptionId) {
        await unsubscribe(appToDelete.subscriptionId)
      } else {
        await deleteTenantApp(appToDelete.id)
      }
      // Refresh the tenant apps list, and the filter values a removed entry may have been the last of
      await queryClient.invalidateQueries({ queryKey: ['tenant-apps'] })
      await queryClient.invalidateQueries({ queryKey: ['tenant-app-filters'] })
      setDeleteModalOpen(false)
      setAppToDelete(null)
    } catch (err: any) {
      setDeleteError(errorText(err, appToDelete.isShortcut ? 'Failed to remove shortcut' : 'Failed to delete package'))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false)
    setAppToDelete(null)
    setDeleteError(null)
  }

  const handleRetryClick = async (app: { id: string; source_job_id?: string }) => {
    if (!app.source_job_id) return

    // Convert to string to ensure consistent comparison
    const appIdStr = String(app.id)
    setRetryingAppId(appIdStr)

    try {
      await retryJob(app.source_job_id)
      // Refresh the tenant apps list to show updated status
      await queryClient.invalidateQueries({ queryKey: ['tenant-apps'] })
    } catch (err: any) {
      console.error('Failed to retry job:', err)
      // Only clear retrying state on error
      setRetryingAppId(null)
    }
  }

  const handleSyncAll = async () => {
    setIsSyncingAll(true)
    setSyncAllMessage(null)
    try {
      const result = await syncAllSubscriptions()
      if (result.synced > 0 || result.failed > 0) {
        // Build detailed message showing what actually happened
        const details = result.details || []
        const tenantAppsUpdated = details.filter((d: any) => d.tenant_app_updated).length
        const intuneSuperseded = details.filter((d: any) => d.intune_superseded).length
        const allWarnings = details.flatMap((d: any) => d.warnings || [])
        const failedDetails = details.filter((d: any) => !d.success)
        let msg = `Processed ${result.synced + result.failed} subscription(s)`
        if (result.synced > 0) msg += `, ${result.synced} synced`
        if (result.failed > 0) msg += `, ${result.failed} failed`
        if (tenantAppsUpdated > 0) msg += ` | ${tenantAppsUpdated} tenant package(s) updated`
        if (intuneSuperseded > 0) msg += ` | ${intuneSuperseded} Intune deployment(s) superseded`
        if (allWarnings.length > 0) msg += ` | Warnings: ${allWarnings.slice(0, 3).join('; ')}`
        // Show failure reasons so user can see what went wrong
        if (failedDetails.length > 0) {
          const reasons = failedDetails.map((d: any) => d.message).slice(0, 3)
          msg += ` | Errors: ${reasons.join('; ')}`
        }
        setSyncAllMessage({
          type: result.failed > 0 && result.synced === 0 ? 'error' : 'success',
          message: msg
        })
        // Refresh app lists
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['public-apps'] }),
          queryClient.invalidateQueries({ queryKey: ['tenant-apps'] }),
        ])
      } else {
        setSyncAllMessage({
          type: 'success',
          message: 'No active subscriptions to sync'
        })
      }
      setTimeout(() => setSyncAllMessage(null), 15000)
    } catch (error: any) {
      setSyncAllMessage({
        type: 'error',
        message: errorText(error, 'Failed to sync subscriptions')
      })
      setTimeout(() => setSyncAllMessage(null), 5000)
    } finally {
      setIsSyncingAll(false)
    }
  }

  const debouncedSearch = searchTerm.trim() || undefined

  // The "Published" key is the preset, not the instant it resolves to, so the key is stable
  // across renders and the instant is taken when the request goes out.
  const filterKey = [debouncedSearch, selectedCategory, publisherFilter, architectureFilter, publishedFilter]
  const filterParams = () => ({
    search: debouncedSearch,
    category: selectedCategory || undefined,
    publisher: publisherFilter || undefined,
    architecture: architectureFilter || undefined,
    published_since: publishedSince(publishedFilter),
  })

  const { data: publicAppsData, isLoading: loadingPublic } = useQuery({
    queryKey: ['public-apps', ...filterKey, publicPage, pageSize],
    queryFn: () => fetchPublicApps({
      ...filterParams(),
      limit: pageSize,
      offset: (publicPage - 1) * pageSize,
    }),
  })

  const { data: tenantAppsData, isLoading: loadingTenant } = useQuery({
    queryKey: ['tenant-apps', ...filterKey, privatePage, pageSize],
    queryFn: () => fetchTenantApps({
      ...filterParams(),
      limit: pageSize,
      offset: (privatePage - 1) * pageSize,
    }),
    enabled: !superAdmin,
  })

  // The values the Category and Publisher filters offer, read across the whole catalog: the list
  // is paged, so one page cannot supply them. A backend without the route leaves the filters at
  // "All …" rather than taking the page down with it.
  const { data: publicFilterValues } = useQuery({
    queryKey: ['public-app-filters'],
    queryFn: () => fetchPublicAppFilters(),
    staleTime: 5 * 60 * 1000,
  })
  const { data: tenantFilterValues } = useQuery({
    queryKey: ['tenant-app-filters'],
    queryFn: () => fetchTenantAppFilters(),
    staleTime: 5 * 60 * 1000,
    enabled: !superAdmin,
  })
  // Both tabs share the filters, so the controls offer both catalogs' values and do not change
  // when the tab does.
  const filterValues: CatalogFilterValues = {
    categories: mergeValues(publicFilterValues?.categories, tenantFilterValues?.categories),
    publishers: mergeValues(publicFilterValues?.publishers, tenantFilterValues?.publishers),
  }

  const isLoading = activeTab === 'public' ? loadingPublic : loadingTenant

  // Extract apps and totals
  // central-backend, which serves the public listing, does not record what a package was built
  // from; tenant-backend's copy of the catalog does, so the page's ids are looked up there.
  const publicPageIds = (publicAppsData?.apps || []).map((app: any) => app.id)
  const { data: publicSourceTypes } = useQuery({
    queryKey: ['public-app-source-types', publicPageIds],
    queryFn: () => getPublicAppSourceTypes(publicPageIds),
    enabled: publicPageIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })
  const publicApps = (publicAppsData?.apps || []).map((app: any) =>
    publicSourceTypes?.[app.id] ? { ...app, source_type: publicSourceTypes[app.id] } : app
  )
  const publicTotal = publicAppsData?.total || 0
  const tenantApps = tenantAppsData?.apps || []
  const tenantTotal = tenantAppsData?.total || 0

  // Clear retrying state when the app status changes to pending
  useEffect(() => {
    if (retryingAppId) {
      const app = tenantApps.find((a: any) => String(a.id) === retryingAppId)
      if (app && app.status === 'pending') {
        setRetryingAppId(null)
      }
    }
  }, [tenantApps, retryingAppId])

  const displayedApps = activeTab === 'public' ? publicApps : tenantApps

  // Bulk deploy: multi-select apps (private + public) and deploy them together.
  const [selectedForDeploy, setSelectedForDeploy] = useState<Map<string, DeployApp>>(new Map())
  const [bulkDeployOpen, setBulkDeployOpen] = useState(false)
  const toggleSelectForDeploy = (app: any) => {
    setSelectedForDeploy(prev => {
      const next = new Map(prev)
      if (next.has(app.id)) next.delete(app.id)
      else
        next.set(app.id, {
          id: app.id,
          name: app.display_name || app.name,
          kind: activeTab === 'public' ? 'public' : 'private',
          // The deploy wizard resolves {Publisher}/{Version}/{Arch} in the naming patterns with
          // these. Without them the Overview shows the pattern where the Intune display name
          // belongs, and the clash check compares against a name nothing will be published as.
          publisher: app.publisher,
          version: app.current_version?.version,
          // The central catalog API reports the architecture inside current_version and has no
          // top-level field for it, so reading only the top level left {Arch} unresolved in the
          // confirmation UI while publish resolved it from the package — the preview disagreed
          // with what was actually created.
          arch: app.architecture || app.current_version?.architecture,
        })
      return next
    })
  }
  const currentPage = activeTab === 'public' ? publicPage : privatePage
  const totalItems = activeTab === 'public' ? publicTotal : tenantTotal
  const totalPages = Math.ceil(totalItems / pageSize)
  const startIndex = (currentPage - 1) * pageSize + 1
  const endIndex = Math.min(currentPage * pageSize, totalItems)

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      if (activeTab === 'public') {
        setPublicPage(page)
      } else {
        setPrivatePage(page)
      }
    }
  }

  // Back to the first page whenever a filter or the page size changes: the old page number
  // would otherwise point past the end of the new result set.
  useEffect(() => {
    setPublicPage(1)
    setPrivatePage(1)
  }, [searchTerm, selectedCategory, publisherFilter, architectureFilter, publishedFilter, pageSize])

  // Switching tenant leaves this page mounted, and the tab is remembered per tenant: re-read it so
  // the new tenant opens on its own tab rather than keeping the previous tenant's.
  useEffect(() => {
    setActiveTab(readCatalogTab(tabKey, superAdmin))
  }, [tabKey, superAdmin])

  /**
   * The wizard opens OVER the catalog and the selection stays visible behind it, as the reference
   * mounts it. It used to replace the catalog behind a breadcrumb, which read as navigating away
   * from the selection you had just made.
   */
  /** What a tile and a row are both given for one package, so they can never describe it differently. */
  const entryProps = (app: any): AppCardProps => ({
    app,
    badge: activeTab === 'public' ? 'Certified' : 'Private',
    isPrivate: activeTab === 'private',
    canDelete: canManageApps && activeTab === 'private',
    onDelete: () => handleDeleteClick(app),
    onRetry: app.status === 'failed' && app.source_job_id ? () => handleRetryClick(app) : undefined,
    isRetrying: retryingAppId !== null && retryingAppId === String(app.id),
  })

  const listColumns = ['Package', 'Publisher', 'Category', 'Architecture', 'Version', 'Published']
  // Plus the selection column when deploying is allowed, plus the actions column.
  const listColumnCount = listColumns.length + (canDeploy ? 1 : 0) + 1
  const tableHead = (
    <thead className="bg-theme-tertiary">
      <tr>
        {canDeploy && <th className="w-10 px-4 py-3" />}
        {listColumns.map((column) => (
          <th key={column} className="px-4 py-3 text-left text-xs font-medium text-theme-text-secondary uppercase">
            {column}
          </th>
        ))}
        <th className="px-4 py-3" />
      </tr>
    </thead>
  )

  const deployWizard = bulkDeployOpen ? (
    <DeployWizard
      isOpen
      onClose={() => setBulkDeployOpen(false)}
      apps={Array.from(selectedForDeploy.values())}
      // Cleared once the job is recorded, which is also when the wizard closes: the selection
      // is what it was rendering, and a selection still ticked after it has been handed to a
      // worker invites the same apps being deployed a second time. How the job then goes is
      // reported by the banner, which outlives this page.
      onStarted={() => setSelectedForDeploy(new Map())}
    />
  ) : null

  return (
    <div className="h-full flex flex-col p-8">
      {deployWizard}
      {/* Header with Add buttons */}
      <div className="shrink-0 flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-theme-text-primary">Catalog</h1>
            <p className="text-theme-text-secondary mt-1">Browse and manage packages</p>
          </div>
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-secondary rounded-lg transition-colors disabled:opacity-50"
            title="Refresh packages"
          >
            <ArrowPathIcon className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {(canManageApps || canDeploy) && (
          <div className="flex gap-3 items-center">
            {/* Deploy selected — side by side with Add Package; disabled with no selection */}
            {canDeploy && (
              <>
                {selectedForDeploy.size > 0 && (
                  <button
                    onClick={() => setSelectedForDeploy(new Map())}
                    className="text-sm text-theme-text-tertiary hover:text-theme-text-primary"
                  >
                    Clear
                  </button>
                )}
                <button
                  onClick={() => setBulkDeployOpen(true)}
                  disabled={selectedForDeploy.size === 0}
                  title={selectedForDeploy.size === 0 ? 'Select one or more packages to deploy' : `Deploy ${selectedForDeploy.size} package(s) to Intune`}
                  className="flex items-center bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                  Deploy{selectedForDeploy.size > 0 ? ` (${selectedForDeploy.size})` : ''}
                </button>
              </>
            )}
            {/* Add Package: pick a source, and the packaging job it starts puts a package in the catalog. */}
            {canManageApps && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                title="Adds a package to the catalog. Agentic Packr builds and tests it."
                className="flex items-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Package
                <ChevronDownIcon className={`w-4 h-4 ml-2 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-theme-card border border-theme-border-primary rounded-lg shadow-lg overflow-hidden z-20">
                  <button
                    onClick={() => handleAddPackage('winget')}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-theme-secondary transition-colors"
                  >
                    <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
                    </svg>
                    <div>
                      <div className="font-medium text-theme-text-primary">WinGet</div>
                      <div className="text-xs text-theme-text-tertiary">Windows Package Manager</div>
                    </div>
                  </button>
                  <button
                    onClick={() => handleAddPackage('choco')}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-theme-secondary transition-colors border-t border-theme-border-primary"
                  >
                    <svg className="w-5 h-5 text-amber-500" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm5.568 16.5c-.393.75-1.092 1.283-1.968 1.5-.516.128-1.068.128-1.584 0-.876-.217-1.575-.75-1.968-1.5l-.048-.09-.048.09c-.393.75-1.092 1.283-1.968 1.5-.516.128-1.068.128-1.584 0-.876-.217-1.575-.75-1.968-1.5C6.144 16.044 6 15.528 6 15c0-1.068.576-2.052 1.5-2.58.324-.186.684-.312 1.068-.372.42-.066.852-.066 1.272 0 .384.06.744.186 1.068.372.168.096.33.204.48.324l.612.504.612-.504c.15-.12.312-.228.48-.324.324-.186.684-.312 1.068-.372.42-.066.852-.066 1.272 0 .384.06.744.186 1.068.372.924.528 1.5 1.512 1.5 2.58 0 .528-.144 1.044-.432 1.5z"/>
                    </svg>
                    <div>
                      <div className="font-medium text-theme-text-primary">Chocolatey</div>
                      <div className="text-xs text-theme-text-tertiary">The Package Manager for Windows</div>
                    </div>
                  </button>
                  <button
                    onClick={handleUploadInstaller}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-theme-secondary transition-colors border-t border-theme-border-primary"
                  >
                    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <div>
                      <div className="font-medium text-theme-text-primary">Upload Installer</div>
                      <div className="text-xs text-theme-text-tertiary">EXE, MSI, or MSIX file</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
            )}
          </div>
        )}
      </div>

      {/* Search and filters, above the tabs: one filter state drives both tabs' queries, and the
          counts in the tab labels are the matches in each catalog. The layout toggle sits at the far
          end of the row. Subscription sync is automatic (update monitor, version-activation sync,
          reconciler), so there is no manual "sync all" here; POST /subscriptions/sync-all stays an
          admin-only operation. */}
      <div className="shrink-0 mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-md min-w-[14rem] flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-tertiary" />
          <input
            type="text"
            placeholder="Search packages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <FilterSelect
          value={selectedCategory || ''}
          onChange={(v) => setSelectedCategory(v || null)}
          label="Filter packages by category"
          options={[
            { value: '', label: 'All categories' },
            ...withCurrent(filterValues.categories, selectedCategory || '').map((c) => ({ value: c, label: c })),
          ]}
        />
        <FilterSelect
          value={publisherFilter}
          onChange={setPublisherFilter}
          label="Filter packages by publisher"
          options={[
            { value: '', label: 'All publishers' },
            ...withCurrent(filterValues.publishers, publisherFilter).map((p) => ({ value: p, label: p })),
          ]}
        />
        <FilterSelect
          value={architectureFilter}
          onChange={setArchitectureFilter}
          label="Filter packages by architecture"
          options={[{ value: '', label: 'All architectures' }, ...ARCHITECTURES.map((a) => ({ value: a, label: a }))]}
        />
        <FilterSelect
          value={publishedFilter}
          onChange={setPublishedFilter}
          label="Filter packages by publish date"
          title="When the package was published to the catalog"
          options={PUBLISHED_WITHIN}
        />
        {hasFilters && (
          <button type="button" onClick={clearFilters} className="text-sm text-blue-500 hover:text-blue-400">
            Clear filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <div role="group" aria-label="Layout" className="flex overflow-hidden rounded-lg border border-theme-border-primary">
            <button
              type="button"
              onClick={() => selectView('tile')}
              aria-pressed={view === 'tile'}
              title="Tiles"
              className={`p-2 transition-colors ${view === 'tile' ? 'bg-blue-600 text-white' : 'bg-theme-input text-theme-text-secondary hover:text-theme-text-primary'}`}
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => selectView('list')}
              aria-pressed={view === 'list'}
              title="List"
              className={`p-2 transition-colors ${view === 'list' ? 'bg-blue-600 text-white' : 'bg-theme-input text-theme-text-secondary hover:text-theme-text-primary'}`}
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs. Each count is that catalog's matches for the search and filters above. */}
      <div className="shrink-0 flex border-b border-theme-border-primary mb-6">
        <button
          onClick={() => selectTab('public')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'public'
              ? 'text-blue-500 border-b-2 border-blue-500'
              : 'text-theme-text-tertiary hover:text-theme-text-secondary'
          }`}
        >
          Public
          {publicTotal > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-theme-tertiary text-theme-text-secondary">
              {publicTotal}
            </span>
          )}
        </button>
        {!superAdmin && (
          <button
            onClick={() => selectTab('private')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'private'
                ? 'text-blue-500 border-b-2 border-blue-500'
                : 'text-theme-text-tertiary hover:text-theme-text-secondary'
            }`}
          >
            Private
            {tenantTotal > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-theme-tertiary text-theme-text-secondary">
                {tenantTotal}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Sync All banner */}
      {syncAllMessage && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          syncAllMessage.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
        }`}>
          {syncAllMessage.message}
        </div>
      )}

      {/* The packages, as tiles or as rows — the only scrolling region of the page */}
      <div className="flex-1 min-h-0 overflow-y-auto">
      {isLoading ? (
        view === 'list' ? (
          <TableCard>
            {tableHead}
            <tbody>
              <TableSkeleton cols={listColumnCount} rows={Math.min(pageSize, 12)} />
            </tbody>
          </TableCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-theme-card border border-theme-border-primary rounded-lg p-6 animate-pulse">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-theme-tertiary rounded-lg"></div>
                </div>
                <div className="h-5 bg-theme-tertiary rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-theme-tertiary rounded w-full mb-2"></div>
                <div className="h-4 bg-theme-tertiary rounded w-2/3"></div>
              </div>
            ))}
          </div>
        )
      ) : displayedApps && displayedApps.length > 0 ? (
        view === 'list' ? (
          <TableCard>
            {tableHead}
            <tbody>
              {displayedApps.map((app: any) => (
                <AppRow
                  key={app.id}
                  {...entryProps(app)}
                  canSelect={canDeploy}
                  selected={selectedForDeploy.has(app.id)}
                  onSelect={() => toggleSelectForDeploy(app)}
                />
              ))}
            </tbody>
          </TableCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayedApps.map((app: any) => (
              <div key={app.id} className="relative h-full">
                {canDeploy && (
                  <label className="absolute top-2 left-2 z-10 flex items-center bg-theme-surface/80 rounded p-1 cursor-pointer"
                         title="Select for bulk deploy" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedForDeploy.has(app.id)} onChange={() => toggleSelectForDeploy(app)} />
                  </label>
                )}
                <AppCard {...entryProps(app)} />
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="bg-theme-card border border-theme-border-primary rounded-lg px-6 py-12 text-center">
          <svg className="mx-auto h-12 w-12 text-theme-text-tertiary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p className="text-theme-text-secondary">
            {activeTab === 'public'
              ? 'No certified packages available yet.'
              : 'No private packages in your organization.'}
          </p>
          {canManageApps && activeTab === 'private' && (
            <button
              onClick={() => navigate('/publish')}
              className="mt-4 text-blue-500 hover:text-blue-400"
            >
              Add your first private app
            </button>
          )}
        </div>
      )}
      </div>

      {/* Pagination Controls */}
      {/* Pagination: the count, the page size and the page controls. Shown whenever there are
          results, so the page size can be changed even when everything fits on one page. */}
      {!isLoading && totalItems > 0 && (
        <div className="shrink-0 mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-theme-border-primary pt-4">
          <div className="text-sm text-theme-text-secondary">
            Showing {startIndex} - {endIndex} of {totalItems} packages
          </div>
          <div className="flex items-center gap-4">
            <FilterSelect
              value={String(pageSize)}
              onChange={(v) => selectPageSize(Number(v) as PageSize)}
              label="Packages per page"
              options={PAGE_SIZES.map((n) => ({ value: String(n), label: `${n} per page` }))}
            />
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-secondary rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first page, last page, current page, and pages around current
                      if (page === 1 || page === totalPages) return true
                      if (Math.abs(page - currentPage) <= 1) return true
                      return false
                    })
                    .map((page, index, arr) => {
                      // Add ellipsis
                      const showEllipsisBefore = index > 0 && page - arr[index - 1] > 1
                      return (
                        <span key={page} className="flex items-center">
                          {showEllipsisBefore && (
                            <span className="px-2 text-theme-text-tertiary">...</span>
                          )}
                          <button
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                              page === currentPage
                                ? 'bg-blue-600 text-white'
                                : 'text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-secondary'
                            }`}
                          >
                            {page}
                          </button>
                        </span>
                      )
                    })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-secondary rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Package Modal (WinGet/Chocolatey) */}
      <AddPackageModal
        isOpen={isPackageModalOpen}
        onClose={() => setIsPackageModalOpen(false)}
        initialSource={packageModalSource}
        lockSource={true}
      />

      {/* Upload Installer Modal */}
      <AddInstallerModal
        isOpen={isInstallerModalOpen}
        onClose={() => setIsInstallerModalOpen(false)}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={handleDeleteCancel}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-theme-card rounded-lg shadow-xl border border-theme-border-primary">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-theme-border-primary">
                <h2 className="text-lg font-semibold text-theme-text-primary">
                  {appToDelete?.isShortcut ? 'Remove Shortcut' : 'Delete Package'}
                </h2>
                <button
                  onClick={handleDeleteCancel}
                  className="text-theme-text-tertiary hover:text-theme-text-primary transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <TrashIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <p className="text-center text-theme-text-primary mb-2">
                  {appToDelete?.isShortcut ? (
                    <>Remove <span className="font-semibold">{appToDelete?.name}</span> from your library?</>
                  ) : (
                    <>Are you sure you want to delete <span className="font-semibold">{appToDelete?.name}</span>?</>
                  )}
                </p>
                <p className="text-center text-theme-text-secondary text-sm mb-4">
                  {appToDelete?.isShortcut
                    ? 'This removes the link to the certified public package. The package stays in the public catalog, and this does not uninstall the app from devices or delete the app in Intune.'
                    : 'This action cannot be undone. All package files and deployment configurations will be permanently removed.'}
                </p>

                {deleteError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                    <p className="text-red-700 dark:text-red-400 text-sm">{deleteError}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-4 border-t border-theme-border-primary">
                <button
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                  className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors flex items-center"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {appToDelete?.isShortcut ? 'Removing...' : 'Deleting...'}
                    </>
                  ) : (
                    appToDelete?.isShortcut ? 'Remove Shortcut' : 'Delete Package'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Why a library entry that links to a certified public package is there. The link glyph says the
 * library holds a link rather than a copy; the word says what put it there. An entry whose reason
 * is not known carries no badge — better than naming a reason we would be guessing at.
 */
const ORIGIN_BADGE = {
  intune: {
    text: 'Intune',
    title: 'Deployed to Intune from this tenant. Your library links to the certified public package.',
    colors: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400',
  },
  conversion: {
    text: 'Conversion',
    title: 'Converted into your library from Discovery. Your library links to the certified public package.',
    colors: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-400',
  },
} as const

interface AppCardProps {
  app: any
  badge: string
  isPrivate?: boolean
  canDelete?: boolean
  onDelete?: () => void
  onRetry?: () => void
  isRetrying?: boolean
}

type EntryState = 'retrying' | 'pending' | 'failed' | 'needs_decision' | 'ready'

/**
 * A newer packaging job of a package that is already published. The package stays deployable
 * while the job runs and after it fails, so this sits beside the status badge, not in its place.
 */
function repackageBadgeOf(app: any): { text: string; title: string; colors: string } | null {
  if (app.repackage?.status === 'running') {
    return {
      text: 'Repackaging...',
      title: 'A new package is being built. The published package stays in use until it is ready.',
      colors: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400',
    }
  }
  if (app.repackage?.status === 'needs_decision') {
    return {
      text: 'Needs your decision',
      title: `The newest package was not fully tested${app.repackage.error_message ? `: ${app.repackage.error_message}` : ''}. Open the job to decide. The published package is unchanged.`,
      colors: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400',
    }
  }
  if (app.not_fully_tested) {
    return {
      text: 'Not fully tested',
      title: app.warning_message || 'This package was published without a passing test.',
      colors: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400',
    }
  }
  if (app.repackage?.status === 'failed') {
    return {
      text: 'Repackage failed',
      title: `The newest packaging job failed${app.repackage.error_message ? `: ${app.repackage.error_message}` : ''}. The published package is unchanged.`,
      colors: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400',
    }
  }
  return null
}

/**
 * What one package's status and kind mean for how it is drawn. The tile and the row both read
 * it, so they cannot disagree on whether an entry is "Certified" or "Private".
 */
export function entryPresentation(app: any, badge: string, isPrivate: boolean, isRetrying: boolean) {
  const isPending = app.status === 'pending' || isRetrying
  const needsDecision = app.status === 'needs_decision' && !isRetrying
  // A package waiting for the user's decision opens its job, as a failed one does.
  const isFailed = (app.status === 'failed' || needsDecision) && !isRetrying
  const state: EntryState = isRetrying ? 'retrying' : isPending ? 'pending'
    : needsDecision ? 'needs_decision' : isFailed ? 'failed' : 'ready'
  // A library entry backed by a subscription: the package lives in the public catalog and this
  // tenant holds a link to it, so nothing here is a package of the tenant's own. The badge names
  // why the entry is there — "Certified" beside it already says the package is the public one.
  const isShortcut = !!app.is_shortcut
  const originBadge = ORIGIN_BADGE[app.origin as keyof typeof ORIGIN_BADGE]
  const isDeployedToIntune = !!app.current_version?.deployment_methods?.some(
    (method: any) => method.method === 'intune' && method.intune_app_id
  )
  const showAutoUpdate = !isPrivate && !!app.auto_update_enabled

  let badgeInfo: { text: string; colors: string }
  if (isRetrying) {
    badgeInfo = { text: 'Retrying...', colors: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400' }
  } else if (isPending) {
    badgeInfo = { text: 'Packaging...', colors: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400' }
  } else if (needsDecision) {
    badgeInfo = { text: 'Needs your decision', colors: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400' }
  } else if (isFailed) {
    badgeInfo = { text: 'Failed', colors: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400' }
  } else {
    // A shortcut points at the certified public package, so it carries that package's badge
    // rather than the "Private" one the tenant's own packages get.
    const text = isShortcut ? 'Certified' : badge
    badgeInfo = {
      text,
      colors: text === 'Certified'
        ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400'
        : 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400',
    }
  }

  const sourceTag = sourceTypeLabel(app)
  const repackageBadge = repackageBadgeOf(app)

  return { state, isPending, isFailed, isShortcut, originBadge, isDeployedToIntune, showAutoUpdate, badgeInfo, sourceTag, repackageBadge }
}

const ICON_BOX: Record<EntryState, string> = {
  retrying: 'bg-blue-100 dark:bg-blue-900/30',
  pending: 'bg-yellow-100 dark:bg-yellow-900/30',
  failed: 'bg-red-100 dark:bg-red-900/30',
  needs_decision: 'bg-amber-100 dark:bg-amber-900/30',
  ready: 'bg-theme-tertiary',
}

/**
 * The package's icon, or what stands in for it while the package is being built or has failed.
 * shrink-0: the icon shares its row with the badges, and a flex item without it is squeezed to a
 * sliver as soon as the badges beside it need the space.
 */
function EntryIcon({ app, state, size }: { app: any; state: EntryState; size: 'lg' | 'sm' }) {
  const box = size === 'lg' ? 'w-12 h-12' : 'w-8 h-8'
  const glyph = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4'
  const letter = size === 'lg' ? 'text-lg' : 'text-sm'
  const spinner = (color: string) => (
    <svg className={`${glyph} ${color} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  )
  return (
    <div className={`${box} shrink-0 rounded-lg flex items-center justify-center overflow-hidden ${ICON_BOX[state]}`}>
      {state === 'retrying' ? (
        spinner('text-blue-600 dark:text-blue-400')
      ) : state === 'pending' ? (
        spinner('text-yellow-600 dark:text-yellow-400')
      ) : state === 'failed' ? (
        <svg className={`${glyph} text-red-600 dark:text-red-400`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ) : app.icon_url ? (
        <>
          <img
            src={app.icon_url.startsWith('/api/apps/') ? app.icon_url.replace('/api/apps/', '/api/tenant/apps/') : app.icon_url}
            alt={app.display_name}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.nextElementSibling?.classList.remove('hidden')
            }}
          />
          <span className={`hidden text-theme-text-primary font-bold ${letter}`}>
            {app.display_name?.charAt(0) || 'A'}
          </span>
        </>
      ) : (
        <span className={`text-theme-text-primary font-bold ${letter}`}>
          {app.display_name?.charAt(0) || 'A'}
        </span>
      )}
    </div>
  )
}

function FilterSelect({ value, onChange, options, label, title }: {
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  label: string
  title?: string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      title={title}
      className="max-w-[14rem] px-3 py-2.5 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  )
}

interface AppRowProps extends AppCardProps {
  canSelect: boolean
  selected: boolean
  onSelect: () => void
}

/** One package as a table row: the tile's facts in columns, with the tile's click behaviour. */
function AppRow({ app, badge, isPrivate = false, canDelete = false, canSelect, selected, onSelect, onDelete, onRetry, isRetrying = false }: AppRowProps) {
  const navigate = useNavigate()
  const { state, isPending, isFailed, isShortcut, originBadge, isDeployedToIntune, showAutoUpdate, badgeInfo, sourceTag, repackageBadge } =
    entryPresentation(app, badge, isPrivate, isRetrying)
  // Controls inside the row act on their own; the row itself opens the package.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation()
  const viewJob = () =>
    navigate('/settings/jobs', app.source_job_id ? { state: { highlightJobId: app.source_job_id } } : undefined)
  const open = () => (isPending || isFailed ? viewJob() : navigate(`/apps/${app.id}`))
  const architecture = app.architecture || app.current_version?.architecture
  const published = app.published_at || app.created_at
  const description = isRetrying
    ? 'Retrying packaging...'
    : isPending
      ? 'Packaging in progress...'
      : stripMarkdown(app.description || '')
  const cell = 'px-4 py-3 text-sm text-theme-text-secondary whitespace-nowrap'

  return (
    <tr onClick={open} className="border-t border-theme-border-primary hover:bg-theme-secondary cursor-pointer">
      {canSelect && (
        <td className="w-10 px-4 py-3" onClick={stop}>
          <input type="checkbox" checked={selected} onChange={onSelect} title="Select for bulk deploy" />
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <EntryIcon app={app} state={state} size="sm" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {isPending || isFailed ? (
                <span className="font-medium text-theme-text-primary">{app.display_name}</span>
              ) : (
                <Link to={`/apps/${app.id}`} onClick={stop} className="font-medium text-theme-text-primary hover:text-blue-500">
                  {app.display_name}
                </Link>
              )}
              {isShortcut && originBadge && !isPending && !isFailed && (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${originBadge.colors}`} title={originBadge.title}>
                  <LinkIcon className="w-3 h-3 mr-1" />
                  {originBadge.text}
                </span>
              )}
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${badgeInfo.colors}`}>{badgeInfo.text}</span>
              {sourceTag && (
                <span className="px-2 py-0.5 text-xs font-medium rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300" title={sourceTypeTitle(app)}>
                  {sourceTag}
                </span>
              )}
              {app.packaging_mode === 'dynamic' && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 rounded" title="Dynamic download - installer is downloaded from vendor site at deployment time. Checksum validated against the tested version.">
                  Dynamic
                </span>
              )}
              {showAutoUpdate && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-400 rounded" title="Auto-update enabled">
                  Auto
                </span>
              )}
              {repackageBadge && (
                <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${repackageBadge.colors}`} title={repackageBadge.title}>
                  {repackageBadge.text}
                </span>
              )}
              {/* Not when the badge above already says Intune — that is the same fact twice. */}
              {isDeployedToIntune && app.origin !== 'intune' && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded" title="Deployed to Microsoft Intune">
                  Intune
                </span>
              )}
            </div>
            {description && <p className="text-xs text-theme-text-secondary truncate max-w-xl">{description}</p>}
          </div>
        </div>
      </td>
      <td className={cell}><span className="block max-w-[12rem] truncate">{app.publisher || 'Unknown'}</span></td>
      <td className={cell}>{app.category || '—'}</td>
      <td className={cell}>{architecture || '—'}</td>
      <td className={cell}>{app.current_version?.version ? `v${app.current_version.version}` : '—'}</td>
      <td className={cell}>{published ? localDate(published, SHORT_DATE) : '—'}</td>
      <td className="px-4 py-3" onClick={stop}>
        <div className="flex items-center justify-end gap-2">
          {isPending || isFailed ? (
            <>
              <button type="button" onClick={viewJob} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300">
                View Job Status
              </button>
              {isFailed && onRetry && !isRetrying && (
                <button type="button" onClick={onRetry} className="flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300">
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  Retry
                </button>
              )}
            </>
          ) : (
            <QuickAddToBundle appId={app.id} appName={app.display_name || app.name} />
          )}
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-1 text-theme-text-tertiary hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
              title={isShortcut ? 'Remove shortcut' : 'Delete package'}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

function AppCard({ app, badge, isPrivate = false, canDelete = false, onDelete, onRetry, isRetrying = false }: AppCardProps) {
  const navigate = useNavigate()
  const { state, isPending, isFailed, isShortcut, originBadge, isDeployedToIntune, showAutoUpdate, badgeInfo, sourceTag, repackageBadge } =
    entryPresentation(app, badge, isPrivate, isRetrying)

  const handleClick = (e: React.MouseEvent) => {
    if (isPending || isFailed) {
      e.preventDefault()
      // Navigate to monitoring/jobs page if there's a source_job_id
      if (app.source_job_id) {
        navigate('/settings/jobs', { state: { highlightJobId: app.source_job_id } })
      } else {
        navigate('/settings/jobs')
      }
    }
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onDelete?.()
  }

  const cardContent = (
    <div className={`bg-theme-card rounded-lg hover:bg-theme-secondary transition-colors p-5 h-full flex flex-col border ${
      isRetrying ? 'border-blue-500/50' : isPending ? 'border-yellow-500/50' : state === 'needs_decision' ? 'border-amber-500/50' : isFailed ? 'border-red-500/50' : 'border-theme-border-primary hover:border-theme-border-secondary'
    }`}>
      <div className="flex items-start justify-between gap-2 mb-4">
        <EntryIcon app={app} state={state} size="lg" />
        <div className="flex flex-wrap items-center justify-end gap-2">
          {isShortcut && originBadge && !isPending && !isFailed && (
            <span
              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${originBadge.colors}`}
              title={originBadge.title}
            >
              <LinkIcon className="w-3 h-3 mr-1" />
              {originBadge.text}
            </span>
          )}
          <span className={`px-2 py-1 text-xs font-medium rounded ${badgeInfo.colors}`}>
            {badgeInfo.text}
          </span>
          {sourceTag && (
            <span className="px-2 py-1 text-xs font-medium rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300" title={sourceTypeTitle(app)}>
              {sourceTag}
            </span>
          )}
          {/* Dynamic download indicator - installer is downloaded from vendor at deploy time */}
          {app.packaging_mode === 'dynamic' && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 rounded" title="Dynamic download - installer is downloaded from vendor site at deployment time. Checksum validated against the tested version.">
              <svg className="w-3 h-3 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Dynamic
            </span>
          )}
          {/* Auto-update indicator for public catalog packages */}
          {showAutoUpdate && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-400 rounded" title="Auto-update enabled">
              <svg className="w-3 h-3 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Auto
            </span>
          )}
          {repackageBadge && (
            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${repackageBadge.colors}`} title={repackageBadge.title}>
              {repackageBadge.text}
            </span>
          )}
{/* Warning tag removed - warnings are shown in app details instead */}
          {!isPending && !isFailed && (
            <QuickAddToBundle appId={app.id} appName={app.display_name || app.name} />
          )}
          {canDelete && (
            <button
              onClick={handleDeleteClick}
              className="p-1 text-theme-text-tertiary hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
              title={isShortcut ? 'Remove shortcut' : 'Delete package'}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <h3 className="text-theme-text-primary font-medium mb-1 truncate">{app.display_name}</h3>
      <p className="text-theme-text-secondary text-sm mb-2 line-clamp-2">
        {isRetrying ? 'Retrying packaging...' : isPending ? 'Packaging in progress...' : isFailed ? stripMarkdown(app.description || '') : (stripMarkdown(app.description || '') || 'No description available')}
      </p>
      {/* Category and Tags */}
      {!isPending && !isFailed && (app.category || (app.tags && app.tags.length > 0)) && (
        <div className="flex flex-wrap gap-1 mb-2">
          {app.category && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
              {app.category}
            </span>
          )}
          {app.tags && app.tags.slice(0, 2).map((tag: string, idx: number) => (
            <span key={idx} className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded">
              {tag}
            </span>
          ))}
          {app.tags && app.tags.length > 2 && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-500">
              +{app.tags.length - 2}
            </span>
          )}
        </div>
      )}
      {/* Publisher/version row sits at the bottom of the card, so tiles whose text runs short
          line up with the rest of the row instead of ending early. */}
      <div className="mt-auto flex items-center justify-between text-sm">
        {isPending || isFailed ? (
          <div className="flex items-center gap-2">
            <button
              onClick={handleClick}
              className={`text-sm font-medium ${isRetrying ? 'text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300' : isPending ? 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-500 dark:hover:text-yellow-300' : 'text-red-600 dark:text-red-400 hover:text-red-500 dark:hover:text-red-300'}`}
            >
              View Job Status
            </button>
            {isFailed && onRetry && !isRetrying && (
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onRetry()
                }}
                className="flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
              >
                <ArrowPathIcon className="w-4 h-4 mr-1" />
                Retry
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 truncate">
              <span className="text-theme-text-tertiary truncate">{app.publisher || 'Unknown'}</span>
              {/* Not when the badge above already says Intune — that is the same fact twice. */}
              {isDeployedToIntune && app.origin !== 'intune' && (
                <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded" title="Deployed to Microsoft Intune">
                  <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                  Intune
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {app.architecture && (
                <span className="text-theme-text-tertiary text-xs bg-theme-tertiary px-1.5 py-0.5 rounded">
                  {app.architecture}
                </span>
              )}
              {app.current_version && (
                <span className="text-theme-text-tertiary">v{app.current_version.version}</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )

  // For pending/failed apps, make the whole card clickable to view job
  if (isPending || isFailed) {
    return (
      <div className="cursor-pointer h-full" onClick={handleClick}>
        {cardContent}
      </div>
    )
  }

  // `h-full` all the way down to the card: a grid item stretches to its row, but only if every
  // wrapper passes that height on. Without it each tile is as tall as its own text.
  return (
    <Link to={`/apps/${app.id}`} className="block h-full">
      {cardContent}
    </Link>
  )
}


function QuickAddToBundle({ appId, appName }: { appId: string; appName: string }) {
  const [show, setShow] = useState(false)
  const [bundles, setBundles] = useState<{ id: string; name: string }[]>([])
  const [selectedBundle, setSelectedBundle] = useState('')
  const [newBundleName, setNewBundleName] = useState('')
  const [action, setAction] = useState('install')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShow(true)
    setSuccess('')
    try {
      const { data } = await tenantAPI.get('/bundles')
      setBundles((data.bundles || []).map((b: any) => ({ id: b.id, name: b.name })))
    } catch {}
  }

  const handleAdd = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setLoading(true)
    try {
      const body: any = { action }
      if (selectedBundle === '__new__') {
        if (!newBundleName.trim()) { setLoading(false); return }
        body.bundle_name = newBundleName.trim()
      } else {
        body.bundle_id = selectedBundle
      }
      const { data } = await tenantAPI.post(`/bundles/add-to-bundle?app_id=${encodeURIComponent(appId)}`, body)
      setSuccess(`Added to "${data.bundle_name}"`)
      setTimeout(() => { setShow(false); setSuccess('') }, 1500)
    } catch (err: any) {
      alert(errorText(err, 'Failed to add to bundle'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        onClick={handleClick}
        className="p-1 text-theme-text-tertiary hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
        title="Add to Bundle"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      </button>

      {show && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShow(false)}>
          <div className="bg-theme-card border border-theme-border-primary rounded-lg p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-theme-text-primary mb-3">
              Add "{appName}" to Bundle
            </h3>
            {success ? (
              <div className="text-green-600 dark:text-green-400 text-center py-3 text-sm">{success}</div>
            ) : (
              <div className="space-y-3">
                <select value={selectedBundle} onChange={e => setSelectedBundle(e.target.value)}
                  className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm">
                  <option value="">Select bundle...</option>
                  {bundles.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  <option value="__new__">+ New bundle</option>
                </select>
                {selectedBundle === '__new__' && (
                  <input type="text" value={newBundleName} onChange={e => setNewBundleName(e.target.value)}
                    placeholder="Bundle name"
                    className="w-full px-3 py-2 bg-theme-input border border-theme-border-primary rounded-lg text-theme-text-primary text-sm" />
                )}
                <div className="flex gap-2">
                  <button onClick={() => setAction('install')}
                    className={`flex-1 py-1.5 rounded text-xs font-medium border ${action === 'install' ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'border-theme-border-primary text-theme-text-tertiary'}`}>Install or update</button>
                  <button onClick={() => setAction('update')}
                    className={`flex-1 py-1.5 rounded text-xs font-medium border ${action === 'update' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' : 'border-theme-border-primary text-theme-text-tertiary'}`}>Update only</button>
                  <button onClick={() => setAction('uninstall')}
                    className={`flex-1 py-1.5 rounded text-xs font-medium border ${action === 'uninstall' ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'border-theme-border-primary text-theme-text-tertiary'}`}>Uninstall</button>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShow(false)} className="px-3 py-1.5 text-theme-text-tertiary text-xs">Cancel</button>
                  <button onClick={handleAdd} disabled={loading || !selectedBundle}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs disabled:opacity-50">
                    {loading ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
